import type { VenueReservation } from "@/graphql/operations";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { GetVenueReservationsRangeQueryResponse } from "./queries/GetVenueReservationsQuery";

type UsePendingReservationsOptions = {
  data?: GetVenueReservationsRangeQueryResponse;
  gracePeriodMs: number;
};

/**
 * Manages pending reservations and their grace period lifecycle.
 */
export default function usePendingReservations({
  data,
  gracePeriodMs,
}: UsePendingReservationsOptions) {
  const [pendingReservations, setPendingReservations] = useState<
    VenueReservation[]
  >([]);
  const pendingRemovalTimeouts = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (!data?.venuePortalUser?.pendingReservations) return;
    setPendingReservations(data.venuePortalUser.pendingReservations);
  }, [data]);

  /**
   * Removes a pending reservation by id.
   */
  const removePendingReservation = useCallback((reservationId: string) => {
    setPendingReservations((prev) =>
      prev.filter((reservation) => reservation.id !== reservationId),
    );
  }, []);

  /**
   * Cancels any scheduled removal for a pending reservation.
   */
  const clearPendingRemovalTimeout = useCallback((reservationId: string) => {
    const timeoutId = pendingRemovalTimeouts.current.get(reservationId);
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
      pendingRemovalTimeouts.current.delete(reservationId);
    }
  }, []);

  /**
   * Schedules removal of a pending reservation after the grace period.
   */
  const schedulePendingRemoval = useCallback(
    (reservationId: string, remainingMs: number) => {
      if (pendingRemovalTimeouts.current.has(reservationId)) return;
      const timeoutId = window.setTimeout(() => {
        pendingRemovalTimeouts.current.delete(reservationId);
        removePendingReservation(reservationId);
      }, remainingMs);
      pendingRemovalTimeouts.current.set(reservationId, timeoutId);
    },
    [removePendingReservation],
  );

  useEffect(() => {
    pendingReservations.forEach((reservation) => {
      if (!reservation.answer) {
        clearPendingRemovalTimeout(reservation.id);
        return;
      }

      const receivedAt = reservation.reservationAnswerReceived
        ? new Date(reservation.reservationAnswerReceived).getTime()
        : Date.now();
      const remainingMs = gracePeriodMs - (Date.now() - receivedAt);
      if (remainingMs <= 0) {
        clearPendingRemovalTimeout(reservation.id);
        removePendingReservation(reservation.id);
        return;
      }

      schedulePendingRemoval(reservation.id, remainingMs);
    });
  }, [
    pendingReservations,
    clearPendingRemovalTimeout,
    removePendingReservation,
    schedulePendingRemoval,
    gracePeriodMs,
  ]);

  useEffect(
    () => () => {
      pendingRemovalTimeouts.current.forEach((timeoutId) =>
        window.clearTimeout(timeoutId),
      );
      pendingRemovalTimeouts.current.clear();
    },
    [],
  );

  const pendingReservationsCount = useMemo(
    () => pendingReservations.filter((reservation) => !reservation.answer).length,
    [pendingReservations],
  );

  return {
    pendingReservations,
    pendingReservationsCount,
    setPendingReservations,
  };
}
